import { useEffect, useState } from "react";

import { useActiveAccount } from "@/components/ActiveAccount";
import { useAuth } from "@/auth/AuthContext";
import { useWebSocketState, useWsSubscribe } from "@/ws/WebSocketProvider";

/**
 * Always-visible status bar above the main content.
 *
 * Surfaces the things a trader checks every few seconds:
 *   * WebSocket connection state (open / connecting / closed)
 *   * Active trading account (login_code · label)
 *   * Live equity (driven by `pnl_update` WS frames)
 *   * Last fill timestamp (driven by `fill` events)
 *   * Server time (server-side `pnl_update.server_ts`, falls back to local)
 *
 * Compact, monospace-style, terminal-aesthetic.
 */
export function StatusRibbon() {
  const { isAuthenticated } = useAuth();
  const { state } = useWebSocketState();
  const { active } = useActiveAccount();
  const [equity, setEquity] = useState<number | null>(null);
  const [dailyPnl, setDailyPnl] = useState<number | null>(null);
  const [lastFillAt, setLastFillAt] = useState<Date | null>(null);
  const [now, setNow] = useState(() => new Date());

  useWsSubscribe("pnl_update", (msg) => {
    if (typeof msg.equity === "number") setEquity(msg.equity);
    if (typeof msg.daily_pnl === "number") setDailyPnl(msg.daily_pnl);
  });
  useWsSubscribe("fill", () => setLastFillAt(new Date()));
  useWsSubscribe("internal_fill", () => setLastFillAt(new Date()));

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(t);
  }, []);

  if (!isAuthenticated) return null;

  return (
    <div className="flex h-7 items-center gap-3 border-b border-term-border bg-term-elev px-3 text-2xs font-mono">
      <ConnectionDot state={state} />
      <span className="text-term-mute">acct</span>
      <span className="text-term-fg">
        {active ? `${active.login_code}${active.label ? ` · ${active.label}` : ""}` : "—"}
      </span>
      <Sep />
      <span className="text-term-mute">equity</span>
      <span className="tnum text-term-fg">
        {equity == null ? "—" : `$${equity.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
      </span>
      <Sep />
      <span className="text-term-mute">daily</span>
      <span
        className={`tnum ${
          dailyPnl == null ? "text-term-fg" : dailyPnl >= 0 ? "up" : "down"
        }`}
      >
        {dailyPnl == null
          ? "—"
          : `${dailyPnl >= 0 ? "+" : ""}$${dailyPnl.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
      </span>
      <Sep />
      <span className="text-term-mute">last fill</span>
      <span className="tnum text-term-dim">
        {lastFillAt ? lastFillAt.toLocaleTimeString() : "—"}
      </span>
      <div className="flex-1" />
      <span className="tnum text-term-dim">{now.toUTCString().slice(17, 25)} UTC</span>
    </div>
  );
}


function ConnectionDot({ state }: { state: "idle" | "connecting" | "open" | "closed" }) {
  const tone =
    state === "open"
      ? "bg-emerald-400"
      : state === "connecting"
        ? "bg-amber-400 animate-pulse"
        : state === "closed"
          ? "bg-red-500"
          : "bg-term-mute";
  const label =
    state === "open" ? "live" : state === "connecting" ? "connecting" : state === "closed" ? "offline" : "idle";
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block h-2 w-2 rounded-full ${tone}`} />
      <span className="uppercase tracking-wider text-term-dim">{label}</span>
    </span>
  );
}


function Sep() {
  return <span className="text-term-mute">·</span>;
}
